import { ZipReader, BlobReader, Uint8ArrayWriter } from "@zip.js/zip.js"
import type { PreparedSkillFile } from "./skill-market-types"

/**
 * 浏览器端解压 zip → PreparedSkillFile[]。
 *
 * 与「文件夹上传」产出同构：path 为 skill 相对路径（剥掉公共顶层文件夹，
 * 使 SKILL.md 落在 manifest 根，与文件夹上传 slice(1) 行为一致）。
 * 服务端只认 { path, content_base64 }[]，因此 controller 无需感知 zip。
 */
export async function extractZipToSkillFiles(file: File): Promise<PreparedSkillFile[]> {
  const reader = new ZipReader(new BlobReader(file))
  try {
    const entries = await reader.getEntries()
    const files: PreparedSkillFile[] = []
    for (const entry of entries) {
      if (entry.directory) continue
      const raw = entry.filename // zip 规范用正斜杠，形如 "my-skill/SKILL.md" 或 "SKILL.md"
      // 过滤 macOS 压缩产生的目录污染
      if (raw.includes("__MACOSX/") || raw.endsWith(".DS_Store")) continue
      const writer = new Uint8ArrayWriter()
      const bytes = await entry.getData(writer)
      files.push({ path: raw, bytes })
    }
    return stripCommonTopFolder(files)
  } finally {
    await reader.close?.()
  }
}

/**
 * 公共顶层文件夹剥离：若所有条目都含 "/" 且共享同一首段（如都形如 "my-skill/..."），
 * 则剥掉首段，使 manifest 根为 SKILL.md（与文件夹上传 slice(1) 一致）。
 * 扁平 zip（条目直接是 "SKILL.md"）不触发剥离。
 */
function stripCommonTopFolder(files: PreparedSkillFile[]): PreparedSkillFile[] {
  if (files.length === 0) return files
  const segs = files.map((f) => f.path.split("/"))
  const allHaveTop = segs.every((s) => s.length > 1)
  if (!allHaveTop) return files
  const top = segs[0][0]
  if (!segs.every((s) => s[0] === top)) return files
  return files.map((f) => ({
    path: f.path.split("/").slice(1).join("/"),
    bytes: f.bytes,
  }))
}
